<script setup lang="ts">
import { computed, watch, ref, onMounted } from 'vue'
import { CLASS_ROOT } from './constants'
import { useI18n } from 'vue-i18n'
import { useUserSession } from '/@src/stores/userSession'
import { neovereneOsobyStore } from '/@src/stores/warningBannerNeovereneOsoby'
import { useRoute } from 'vue-router'
import { IDSKHeaderVariant } from '/@src/composable/componentPropsTypes'
import IDSKContainer from '/@src/components/grid/IDSKContainer.vue'
import * as useFeatureFlags from '/@src/composable/useFeatureFlags'
import * as apis from '/@src/client-public/api'
import { BannerPublicResponse } from '/@src/client-public'

export interface IDSKHeaderProps {
  isSimple?: boolean
  variant: IDSKHeaderVariant
  backButton?: string
}

const emit = defineEmits(['close'])

const props = withDefaults(defineProps<IDSKHeaderProps>(), {
  backButton: 'Naspäť',
})

const ROUTES_WITHOUT_BANNER_OVERENIE = ['vyber-subjektu']
const mobileMenuActive = ref<boolean>(false)
const { t } = useI18n()
const userSession = useUserSession()
const neovereneOsobyActions = neovereneOsobyStore()
const bannerApi = new apis.PublicBannerApi()
const bannerInfo = ref<BannerPublicResponse>()

const getBannerText = (): string | null => {
  const text = document.getElementsByName('IMPORTANT_NOTICE')[0].getAttribute('content')
  if (text == '${IMPORTANT_NOTICE}') {
    //development
    return null
  }
  return text
}

const getNoticetype = (): string | null => {
  const noticeType = document
    .getElementsByName('IMPORTANT_NOTICE_TYPE')[0]
    .getAttribute('content')
  if (noticeType === '${IMPORTANT_NOTICE_TYPE}') {
    return 'warning'
  }
  return noticeType ? noticeType : 'warning'
}

const linkToSupport: any = document
  .getElementsByName('SERVICE_TICKET_URL')[0]
  .getAttribute('content')
const isWarningVisibleForPage = ref<boolean | any>(true)
const route = useRoute()

const NAV_ITEMS = computed(() => {
  // Vytvorte základné pole
  const items = [
    {
      title: t('invitations'),
      href: '/app/vyzvy',
      insideAppLink: true,
    },
    ...(props.variant === IDSKHeaderVariant.loggedIn
      ? [
          {
            title: t('ziadosti.titleMenu'),
            href: '/app/ziadosti',
            insideAppLink: true,
          },
        ]
      : []),
  ]

  if (useFeatureFlags.isFeatureFlag(useFeatureFlags.FeatureFlags.ZOP)) {
    props.variant === IDSKHeaderVariant.loggedIn
      ? items.splice(
          2,
          0,
          {
            title: t('projekty.menu-title'),
            href: '/app/projekty',
            insideAppLink: true,
          },
          {
            title: t('zop.menu-title'),
            href: '/app/zop',
            insideAppLink: true,
          }
        )
      : []
  }

  // Vráťte upravené pole
  return items.flat()
})

watch(
  () => userSession?.user,
  async (curr) => {
    neovereneOsobyActions.checkIfBannerVisible(curr)
  }
)

async function getInfoBanner() {
  try {
    const response = await bannerApi.ziskajBannerPublicBanner()
    bannerInfo.value = response?.data
  } catch (error) {
    console.log(error)
  }
}

onMounted(async () => {
  await getInfoBanner()
  neovereneOsobyActions.checkIfBannerVisible(userSession?.user)
  if (ROUTES_WITHOUT_BANNER_OVERENIE.includes(<string>route.name)) {
    isWarningVisibleForPage.value = false
  } else {
    isWarningVisibleForPage.value = true
  }
})
</script>

<template>
  <header :class="CLASS_ROOT">
    <div :class="`${CLASS_ROOT}__scrolling-wrapper`">
      <IDSKBanner
        v-if="typeof bannerInfo?.text === 'string'"
        :text="bannerInfo?.text"
        :type="bannerInfo?.bannerTyp"
      />
      <IDSKWarningText
        v-if="
          userSession?.user?.subjekt?.typ === 'ORGANIZACIA' &&
          neovereneOsobyActions.isBannerVisibleForUser?.data &&
          isWarningVisibleForPage
        "
        class="mb-0 pl-0 d-flex align-items-center"
        :icon-present="false"
      >
        <template #default>
          <i18n-t keypath="globalne.neovereneOsobyWarning.uvod" :tag="false">
            <template #overte>
              <IDSKRouterLink
                href="/app/neovereneOsobyNaSubjekte#overte"
                data-test-id="link-osoby-overenie"
              >
                {{ t('globalne.neovereneOsobyWarning.overte') }}
              </IDSKRouterLink>
            </template>
          </i18n-t>
          <IDSKRouterLink
            href="/app/neovereneOsobyNaSubjekte#pozastavte"
            data-test-id="link-osoby-pozastavit"
          >
            {{ t('globalne.neovereneOsobyWarning.pozastavte') }}
          </IDSKRouterLink>
          .
        </template>
      </IDSKWarningText>
      <IDSKTricolor />
      <IDSKHeaderBrand />
      <div :class="`${CLASS_ROOT}__main`">
        <IDSKContainer>
          <IDSKRow>
            <IDSKCol variant="full" variant-desktop="one-third">
              <div :class="`${CLASS_ROOT}__main-headline`">
                <a
                  :href="route.name === 'vyber-subjektu' ? '#' : '/'"
                  title="Odkaz na úvodnú stránku"
                  data-test-id="link-uvodna-stranka"
                >
                  <img
                    class="ispo-logo"
                    src="/ispo-logo.svg"
                    alt="Informačný systém plánu obnovy"
                    loading="eager"
                  />
                </a>
                <button
                  :class="[
                    'idsk-button',
                    `${CLASS_ROOT}__main-headline-menu-button`,
                    {
                      [`${CLASS_ROOT}__main-headline-menu-button--active`]:
                        mobileMenuActive,
                    },
                  ]"
                  data-test-id="button-rozbalit-menu"
                  :aria-label="mobileMenuActive ? 'Zavrieť menu' : 'Rozbaliť menu'"
                  :aria-expanded="mobileMenuActive"
                  @click="mobileMenuActive = !mobileMenuActive"
                >
                  {{ mobileMenuActive ? 'Zavrieť' : 'Menu' }}
                  <span class="idsk-header-web__menu-open"></span>
                  <span class="idsk-header-web__menu-close"></span>
                </button>
              </div>
            </IDSKCol>

            <IDSKCol v-if="!props.isSimple" variant="two-thirds">
              <div :class="`${CLASS_ROOT}__main-action`">
                <div :class="`${CLASS_ROOT}__main--buttons`">
                  <div :class="`${CLASS_ROOT}__main--login`">
                    <Prihlasenie />
                  </div>
                </div>
              </div>
            </IDSKCol>
          </IDSKRow>
        </IDSKContainer>
      </div>
      <template v-if="!props.isSimple && props.variant !== IDSKHeaderVariant.modal">
        <IDSKNavDivider />
        <IDSKNav :items="NAV_ITEMS" :mobile-opened="mobileMenuActive" />
      </template>
      <template v-else-if="!props.isSimple && props.variant === IDSKHeaderVariant.modal">
        <IDSKNavDivider />
        <IDSKContainer>
          <IDSKRow>
            <IDSKCol>
              <a
                class="mb-6 govuk-back-link"
                href="#"
                data-test-id="link-spat-modal"
                @click="emit('close')"
                @keyup.enter="emit('close')"
              >
                {{ props.backButton }}
              </a></IDSKCol
            ></IDSKRow
          >
        </IDSKContainer>
      </template>
    </div>
  </header>
</template>

<style lang="scss" scoped>
.idsk-header-web {
  @media (min-width: 767px) {
    box-shadow: none;
  }

  &__scrolling-wrapper {
    background: var(--idsk-color-white);
  }
}

.ispo-logo {
  height: 40px;
  transition: all 0.275s ease-out;

  @include govuk-media-query($from: desktop) {
    height: 60px;
  }

  &:hover {
    scale: 1.05;
  }
  &:active {
    scale: 1;
  }
}

.idsk-header-web__main-headline {
  margin: 16px 0 !important;
}
</style>

<i18n lang="yaml">
sk:
  invitations: 'Výzvy'
  requests: 'Moje žiadosti'
en:
  invitations: 'Invitations'
  requests: 'Requests'
</i18n>
